import React, { useState, useRef, useEffect, useMemo } from "react";
import { ChevronDown, Search, Check } from "lucide-react";

export function CustomSelect({
  options = [],
  value,
  onChange,
  placeholder = "Select option",
  disabled = false,
  className = "",
  size = "md",
  searchable,
  searchPlaceholder = "Search...",
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);

  const normalizedOptions = useMemo(() => {
    return options.map((opt) => {
      if (typeof opt === "object" && opt !== null) {
        return {
          value: opt.value ?? opt.id ?? "",
          label: opt.label ?? opt.name ?? String(opt.value ?? ""),
          subtitle: opt.subtitle ?? opt.address ?? "",
        };
      }
      return {
        value: String(opt),
        label: String(opt),
        subtitle: "",
      };
    });
  }, [options]);

  const shouldSearch = searchable ?? normalizedOptions.length > 5;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && shouldSearch) {
      setTimeout(() => searchInputRef.current?.focus(), 40);
    } else {
      setSearchQuery("");
    }
  }, [isOpen, shouldSearch]);

  const selectedOption = normalizedOptions.find(
    (opt) => String(opt.value).toLowerCase() === String(value ?? "").toLowerCase()
  );

  const displayedOptions = useMemo(() => {
    if (!shouldSearch || !searchQuery.trim()) return normalizedOptions;
    const q = searchQuery.toLowerCase().trim();
    return normalizedOptions.filter(
      (opt) =>
        opt.label.toLowerCase().includes(q) ||
        opt.subtitle.toLowerCase().includes(q)
    );
  }, [normalizedOptions, shouldSearch, searchQuery]);

  const isSm = size === "sm";

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between gap-2 rounded-md border border-gray-200 bg-white font-normal text-left text-[#1b1b23] transition-all outline-none hover:border-gray-300 focus:border-[#152736] focus:ring-1 focus:ring-[#152736] disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed ${
          isSm ? "h-9 px-3 text-xs" : "h-10 px-3.5 text-sm"
        }`}
      >
        <span className="truncate">
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown
          className={`h-4 w-4 text-gray-400 transition-transform flex-shrink-0 ${
            isOpen ? "rotate-180 text-gray-600" : ""
          }`}
        />
      </button>

      {isOpen && !disabled && (
        <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-60 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg flex flex-col min-w-[200px]">
          {shouldSearch && (
            <div className="p-2 border-b border-gray-100 bg-gray-50/70">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-2.5 h-3.5 w-3.5 text-gray-400" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={searchPlaceholder}
                  className="w-full rounded border border-gray-200 bg-white py-1.5 pl-8 pr-3 text-xs outline-none focus:border-[#152736]"
                />
              </div>
            </div>
          )}

          <div className="overflow-y-auto max-h-52 divide-y divide-gray-50">
            {displayedOptions.length === 0 ? (
              <div className="px-3 py-3 text-xs text-center text-gray-400">
                No options found
              </div>
            ) : (
              displayedOptions.map((opt) => {
                const isSelected =
                  String(opt.value).toLowerCase() === String(value ?? "").toLowerCase();
                return (
                  <button
                    key={String(opt.value)}
                    type="button"
                    onClick={() => {
                      onChange(opt.value);
                      setIsOpen(false);
                    }}
                    className={`w-full flex items-center justify-between gap-2 px-3.5 py-2 text-left text-sm transition-colors ${
                      isSelected
                        ? "bg-blue-50/70 text-blue-700 font-medium"
                        : "text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="truncate">{opt.label}</div>
                      {opt.subtitle ? (
                        <div className="text-[11px] text-gray-400 truncate">
                          {opt.subtitle}
                        </div>
                      ) : null}
                    </div>
                    {isSelected && (
                      <Check className="h-4 w-4 text-blue-600 shrink-0" />
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default CustomSelect;
